"use client";

import React, { useState } from "react";

import { ChevronDown, ChevronRight } from "lucide-react";

// ---------------------------------------------------------------------------
// JSON Schema type helpers
// ---------------------------------------------------------------------------

type SchemaDefs = Record<string, Record<string, unknown>>;

type PropertySchema = {
  type?: string;
  anyOf?: Array<Record<string, unknown>>;
  items?: Record<string, unknown>;
  $ref?: string;
  additionalProperties?: Record<string, unknown>;
  description?: string;
  title?: string;
  enum?: string[];
  properties?: Record<string, Record<string, unknown>>;
  required?: string[];
};

interface ResolvedType {
  kind:
    | "string"
    | "number"
    | "boolean"
    | "enum"
    | "string_array"
    | "enum_array"
    | "object"
    | "object_array"
    | "dict"
    | "unknown";
  enumValues?: string[];
  objectSchema?: PropertySchema;
  description?: string;
  title?: string;
}

function resolveRef(ref: string, defs: SchemaDefs): PropertySchema {
  const name = ref.replace("#/$defs/", "");
  return (defs as Record<string, PropertySchema>)[name] ?? {};
}

function resolveType(prop: PropertySchema, defs: SchemaDefs): ResolvedType {
  const base: Pick<ResolvedType, "description" | "title"> = {
    description: prop.description,
    title: prop.title,
  };

  if (prop.$ref) {
    const resolved = resolveRef(prop.$ref, defs);
    if (resolved.enum) return { ...base, kind: "enum", enumValues: resolved.enum };
    if (resolved.type === "object") return { ...base, kind: "object", objectSchema: resolved };
    return { ...base, kind: "string" };
  }

  const candidates = prop.anyOf?.filter((s) => (s as { type?: string }).type !== "null") ?? [];
  const inner = (candidates[0] ?? prop) as PropertySchema;

  if (inner.$ref) {
    const resolved = resolveRef(inner.$ref, defs);
    if (resolved.enum) return { ...base, kind: "enum", enumValues: resolved.enum };
    if (resolved.type === "object") return { ...base, kind: "object", objectSchema: resolved };
    return { ...base, kind: "string" };
  }

  if (inner.type === "string") return { ...base, kind: "string" };
  if (inner.type === "boolean") return { ...base, kind: "boolean" };
  if (inner.type === "number" || inner.type === "integer") return { ...base, kind: "number" };

  if (inner.type === "array") {
    const items = inner.items as PropertySchema | undefined;
    if (items?.$ref) {
      const resolved = resolveRef(items.$ref, defs);
      if (resolved.enum) return { ...base, kind: "enum_array", enumValues: resolved.enum };
      if (resolved.type === "object")
        return { ...base, kind: "object_array", objectSchema: resolved };
    }
    return { ...base, kind: "string_array" };
  }

  if (inner.type === "object" && inner.additionalProperties) {
    return { ...base, kind: "dict" };
  }

  if (inner.type === "object") {
    return { ...base, kind: "object", objectSchema: inner };
  }

  return { ...base, kind: "unknown" };
}

// ---------------------------------------------------------------------------
// Display configuration
// ---------------------------------------------------------------------------

export interface FieldGroup {
  label: string;
  fields: string[];
}

export interface SchemaDisplayConfig {
  enumIcons?: Record<string, string>;
  colorFields?: Set<string>;
  resolveColorHex?: (value: string) => string | undefined;
}

// ---------------------------------------------------------------------------
// Sub-renderers
// ---------------------------------------------------------------------------

function NullBadge() {
  return <span className="italic text-gray-600">—</span>;
}

function TagPill({
  children,
  icon,
  className = "",
}: {
  children: React.ReactNode;
  icon?: string;
  className?: string;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${className}`}
      style={{ backgroundColor: "rgba(255,255,255,0.1)", color: "#e2e8f0" }}
    >
      {icon && <span className="text-[11px]">{icon}</span>}
      {children}
    </span>
  );
}

function ColorSwatch({ color, hex }: { color: string; hex?: string }) {
  const bg = hex ?? color;
  return (
    <span
      className="inline-flex items-center gap-1.5 rounded-full px-2 py-0.5"
      style={{ backgroundColor: "rgba(255,255,255,0.07)" }}
    >
      <span
        className="inline-block h-3 w-3 rounded-full border"
        style={{ backgroundColor: bg, borderColor: "rgba(255,255,255,0.25)" }}
      />
      <span className="text-[11px] text-gray-300">{color}</span>
      {hex && <span className="text-[10px] text-gray-500">{hex}</span>}
    </span>
  );
}

function BooleanBadge({ value }: { value: boolean }) {
  return (
    <span
      className="inline-block rounded px-1.5 py-0.5 text-[11px] font-semibold"
      style={{
        backgroundColor: value ? "rgba(16,185,129,0.2)" : "rgba(239,68,68,0.2)",
        color: value ? "#6ee7b7" : "#fca5a5",
      }}
    >
      {value ? "true" : "false"}
    </span>
  );
}

function NumberValue({ value }: { value: number }) {
  return <span className="font-mono text-[12px] text-blue-300">{String(value)}</span>;
}

function StringValue({ value }: { value: string }) {
  return <span className="text-[12px] text-gray-200">{value}</span>;
}

function TagList({
  values,
  isColor = false,
  showIcons = false,
  config,
}: {
  values: string[];
  isColor?: boolean;
  showIcons?: boolean;
  config?: SchemaDisplayConfig;
}) {
  if (values.length === 0) return <NullBadge />;
  return (
    <div className="flex flex-wrap gap-1.5">
      {values.map((v, i) => {
        const hex = config?.resolveColorHex?.(v);
        if (isColor && hex) {
          return <ColorSwatch key={`${v}-${i}`} color={v} hex={hex} />;
        }
        const icon = showIcons ? config?.enumIcons?.[v] : undefined;
        return (
          <TagPill key={`${v}-${i}`} icon={icon}>
            {v}
          </TagPill>
        );
      })}
    </div>
  );
}

function stripHandlePrefix(value: string): string {
  const idx = value.indexOf("__");
  return idx >= 0 ? value.slice(idx + 2).replaceAll("_", " ") : value.replaceAll("_", " ");
}

function formatDictValue(value: unknown): string {
  if (value === null || value === undefined) return "—";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (Array.isArray(value)) return value.map((v) => String(v)).join(", ");
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function DictTable({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data).filter(([, v]) => v !== null && v !== undefined);
  if (entries.length === 0) return <NullBadge />;
  return (
    <div className="space-y-1.5">
      {entries.map(([key, value]) => (
        <div key={key} className="flex flex-wrap items-start gap-2">
          <span className="shrink-0 text-[11px] font-medium text-gray-400">
            {key.replaceAll("_", " ")}:
          </span>
          <div className="flex flex-wrap gap-1">
            {Array.isArray(value) ? (
              value.map((v, i) => (
                <TagPill key={`${String(v)}-${i}`}>{stripHandlePrefix(String(v))}</TagPill>
              ))
            ) : (
              <span className="text-[11px] text-gray-300">{formatDictValue(value)}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function objectDisplayLabel(item: unknown): string {
  if (typeof item !== "object" || item === null) return String(item);
  const obj = item as Record<string, unknown>;
  if (typeof obj.name === "string") return obj.name;
  if (typeof obj.label === "string") return obj.label;
  if (typeof obj.title === "string") return obj.title;
  return JSON.stringify(obj);
}

function isUrl(value: string): boolean {
  return /^https?:\/\/.+/i.test(value);
}

function UrlValue({ value }: { value: string }) {
  return (
    <a
      href={value}
      target="_blank"
      rel="noopener noreferrer"
      className="break-all text-[12px] text-blue-400 underline decoration-blue-400/40 hover:text-blue-300"
    >
      {value} ↗
    </a>
  );
}

function ObjectFieldValue({ fieldKey, value }: { fieldKey: string; value: unknown }) {
  if (Array.isArray(value)) return <TagList values={value.map(objectDisplayLabel)} />;
  if (typeof value === "object" && value !== null) {
    return <span className="text-[11px] text-gray-300">{JSON.stringify(value)}</span>;
  }

  const strVal = String(value);

  if (fieldKey === "brand_slug" && strVal) {
    return (
      <a
        href={`/brands/${encodeURIComponent(strVal)}`}
        target="_blank"
        rel="noopener noreferrer"
        className="text-[12px] text-blue-400 underline decoration-blue-400/40 hover:text-blue-300"
      >
        {strVal} ↗
      </a>
    );
  }

  if (isUrl(strVal)) return <UrlValue value={strVal} />;

  return <StringValue value={strVal} />;
}

function isImageLikeObject(
  data: Record<string, unknown>
): data is { url: string; height?: number | null; width?: number | null } {
  return typeof data.url === "string" && isUrl(data.url);
}

function ImageCard({ data }: { data: Record<string, unknown> }) {
  const url = data.url as string;
  const width = data.width as number | null | undefined;
  const height = data.height as number | null | undefined;
  const dims = width != null && height != null ? `${width}×${height}` : null;

  return (
    <div
      className="flex items-start gap-3 rounded-md border px-3 py-2"
      style={{ borderColor: "rgba(255,255,255,0.1)", backgroundColor: "rgba(255,255,255,0.03)" }}
    >
      <img
        src={url}
        alt=""
        className="h-12 w-12 shrink-0 rounded border object-cover"
        style={{ borderColor: "rgba(255,255,255,0.15)" }}
        loading="lazy"
      />
      <div className="min-w-0 flex-1 space-y-0.5">
        <UrlValue value={url} />
        {dims && <div className="text-[10px] text-gray-500">{dims}px</div>}
      </div>
    </div>
  );
}

function ObjectCard({ data, schema }: { data: Record<string, unknown>; schema: PropertySchema }) {
  if (isImageLikeObject(data)) return <ImageCard data={data} />;

  const props = schema.properties ?? {};
  return (
    <div
      className="rounded-md border px-3 py-2"
      style={{ borderColor: "rgba(255,255,255,0.1)", backgroundColor: "rgba(255,255,255,0.03)" }}
    >
      {Object.keys(props).map((key) => {
        const val = data[key];
        if (val === null || val === undefined) return null;
        return (
          <div key={key} className="flex items-baseline gap-2 py-0.5">
            <span className="shrink-0 text-[11px] font-medium text-gray-400">{key}:</span>
            <ObjectFieldValue fieldKey={key} value={val} />
          </div>
        );
      })}
    </div>
  );
}

function isAttributeLikeArray(
  items: Array<Record<string, unknown>>
): items is Array<{ name: string; handle: string; values: Array<{ name: string }> }> {
  const first = items[0];
  return !!first && typeof first.name === "string" && Array.isArray(first.values);
}

function ObjectArrayRenderer({
  items,
  schema,
}: {
  items: Array<Record<string, unknown>>;
  schema: PropertySchema;
}) {
  if (items.length === 0) return <NullBadge />;

  if (isAttributeLikeArray(items)) {
    return (
      <div className="space-y-1.5">
        {items.map((attr) => (
          <div key={attr.handle} className="flex flex-wrap items-start gap-2">
            <span className="shrink-0 text-[11px] font-medium text-gray-400">{attr.name}:</span>
            <div className="flex flex-wrap gap-1">
              {attr.values.map((v, i) => (
                <TagPill key={`${v.name}-${i}`}>{v.name}</TagPill>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {items.map((item, i) => (
        <ObjectCard key={i} data={item} schema={schema} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Field renderer: dispatches to the right sub-renderer based on resolved type
// ---------------------------------------------------------------------------

function FieldRenderer({
  fieldName,
  value,
  resolved,
  config,
}: {
  fieldName: string;
  value: unknown;
  resolved: ResolvedType;
  config?: SchemaDisplayConfig;
}) {
  if (value === null || value === undefined) return <NullBadge />;

  const isColorField = config?.colorFields?.has(fieldName) ?? false;

  switch (resolved.kind) {
    case "string":
      return <StringValue value={String(value)} />;
    case "number":
      return <NumberValue value={value as number} />;
    case "boolean":
      return <BooleanBadge value={value as boolean} />;
    case "enum": {
      const strVal = String(value);
      const icon = config?.enumIcons?.[strVal];
      return <TagPill icon={icon}>{strVal}</TagPill>;
    }
    case "string_array":
      return (
        <TagList values={value as string[]} isColor={isColorField} showIcons config={config} />
      );
    case "enum_array":
      return (
        <TagList values={value as string[]} isColor={isColorField} showIcons config={config} />
      );
    case "object": {
      const objSchema = resolved.objectSchema ?? {};
      return <ObjectCard data={value as Record<string, unknown>} schema={objSchema} />;
    }
    case "object_array":
      return (
        <ObjectArrayRenderer
          items={value as Array<Record<string, unknown>>}
          schema={resolved.objectSchema ?? {}}
        />
      );
    case "dict":
      return <DictTable data={value as Record<string, unknown>} />;
    default:
      return <pre className="text-[11px] text-gray-400">{JSON.stringify(value, null, 2)}</pre>;
  }
}

// ---------------------------------------------------------------------------
// Section component (collapsible)
// ---------------------------------------------------------------------------

function Section({
  label,
  children,
  defaultOpen = true,
}: {
  label: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-1.5 py-1.5 text-left"
      >
        {open ? (
          <ChevronDown className="h-3.5 w-3.5 text-gray-500" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-gray-500" />
        )}
        <span className="text-[11px] font-bold uppercase tracking-widest text-gray-400">
          {label}
        </span>
      </button>
      {open && <div className="space-y-2 pb-3 pl-5">{children}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main exported component
// ---------------------------------------------------------------------------

interface SchemaRendererProps {
  schema: {
    $defs?: Record<string, Record<string, unknown>>;
    properties?: Record<string, Record<string, unknown>>;
  };
  data: Record<string, unknown>;
  fieldGroups?: FieldGroup[];
  displayConfig?: SchemaDisplayConfig;
}

export function SchemaRenderer({ schema, data, fieldGroups, displayConfig }: SchemaRendererProps) {
  const defs = (schema.$defs ?? {}) as SchemaDefs;
  const properties = (schema.properties ?? {}) as Record<string, PropertySchema>;

  const groups = fieldGroups ?? [];
  const groupedFieldNames = new Set(groups.flatMap((g) => g.fields));
  const ungroupedFields = Object.keys(properties).filter((k) => !groupedFieldNames.has(k));

  const renderField = (fieldName: string) => {
    const propSchema = properties[fieldName];
    if (!propSchema) return null;
    const resolved = resolveType(propSchema, defs);
    const value = data[fieldName];

    return (
      <div key={fieldName} className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
        <span className="shrink-0 text-[11px] font-medium text-gray-500">
          {resolved.title ?? fieldName}
          {resolved.description && (
            <span className="cursor-help text-gray-600" title={resolved.description}>
              {" "}
              ?
            </span>
          )}
          :
        </span>
        <FieldRenderer
          fieldName={fieldName}
          value={value}
          resolved={resolved}
          config={displayConfig}
        />
      </div>
    );
  };

  return (
    <div className="space-y-1">
      {groups.map((group) => {
        const hasAnyValue = group.fields.some((f) => data[f] !== null && data[f] !== undefined);
        if (!hasAnyValue) return null;

        return (
          <Section key={group.label} label={group.label}>
            {group.fields.map(renderField)}
          </Section>
        );
      })}

      {ungroupedFields.length > 0 && (
        <Section
          label={groups.length > 0 ? "Other" : "All Fields"}
          defaultOpen={groups.length === 0}
        >
          {ungroupedFields.map(renderField)}
        </Section>
      )}
    </div>
  );
}
